import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, Shield, Star, Ban, CheckCircle2, UserMinus, UserCheck, Flame } from "lucide-react";

const ROLES = ["admin", "warrior", "member"];

function badgeFor(role) {
    if (role === "admin") return { bg: "#7F1D1D", color: "#FDFBF7", label: "Admin" };
    if (role === "warrior") return { bg: "#1E1B4B", color: "#D4AF37", label: "Warrior" };
    return { bg: "#F5F0E6", color: "#1E1B4B", label: "Member" };
}

export default function UserManagement({ currentUserId }) {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
        setUsers(data);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const filtered = useMemo(() => users, [users]);

    const act = async (uid, fn) => {
        setBusyId(uid); setError("");
        try { await fn(); await load(); }
        catch (e) { setError(e.response?.data?.detail || e.message); }
        finally { setBusyId(null); }
    };

    const setRole = (u, role) => act(u.id, () => api.post(`/admin/users/${u.id}/role`, { role }));
    const setActive = (u, is_active) => act(u.id, () => api.post(`/admin/users/${u.id}/active`, { is_active }));
    const setFeatured = (u, featured) => act(u.id, () => api.post(`/admin/users/${u.id}/featured`, { featured }));

    return (
        <div className="p-card p-6" data-testid="admin-users-section">
            <div className="flex items-center gap-2 mb-4">
                <Shield size={16} style={{ color: "#7F1D1D" }} />
                <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>User Management</div>
                <div className="ml-auto relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7F1D1D" }} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                           placeholder="Search by name or email"
                           className="pl-9 pr-4 py-2 rounded-full border text-sm w-64"
                           style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                           data-testid="admin-users-search" />
                </div>
            </div>

            {error && <div className="text-sm mb-3" style={{ color: "#7F1D1D" }} data-testid="admin-users-error">{error}</div>}

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs tracking-[0.18em] uppercase" style={{ color: "#7F1D1D" }}>
                            <th className="py-2 pr-3">Member</th>
                            <th className="py-2 pr-3">Role</th>
                            <th className="py-2 pr-3">Status</th>
                            <th className="py-2 pr-3">Featured</th>
                            <th className="py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((u) => {
                            const b = badgeFor(u.role);
                            const isMe = u.id === currentUserId;
                            const inactive = u.is_active === false;
                            return (
                                <tr key={u.id} className="border-t" style={{ borderColor: "#E5E0D8" }} data-testid={`admin-users-row-${u.id}`}>
                                    <td className="py-3 pr-3">
                                        <div className="flex items-center gap-2.5">
                                            <div className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                                                 style={{ background: "#1E1B4B", color: "#D4AF37", fontSize: 12 }}>
                                                {u.photo_url ? <img src={u.photo_url} alt={u.name} className="h-full w-full object-cover" />
                                                            : <span>{(u.name || "U").charAt(0).toUpperCase()}</span>}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium truncate" style={{ color: "#1E1B4B" }}>{u.name}{isMe && <span className="ml-1 text-xs" style={{ color: "#7F1D1D" }}>(you)</span>}</div>
                                                <div className="text-xs truncate" style={{ color: "#525252" }}>{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 pr-3">
                                        <span className="text-[11px] tracking-[0.18em] uppercase px-2 py-1 rounded-full" style={{ background: b.bg, color: b.color }}>
                                            {b.label}
                                        </span>
                                    </td>
                                    <td className="py-3 pr-3">
                                        {inactive ? (
                                            <span className="text-[11px] tracking-[0.18em] uppercase px-2 py-1 rounded-full" style={{ background: "#F5F0E6", color: "#7F1D1D" }}>Deactivated</span>
                                        ) : (
                                            <span className="text-[11px] tracking-[0.18em] uppercase px-2 py-1 rounded-full" style={{ background: "#F5F0E6", color: "#1E1B4B" }}>Active</span>
                                        )}
                                    </td>
                                    <td className="py-3 pr-3">
                                        {u.featured ? (
                                            <span className="text-[11px] tracking-[0.18em] uppercase px-2 py-1 rounded-full inline-flex items-center gap-1"
                                                  style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                                                <Star size={10} /> Featured
                                            </span>
                                        ) : <span className="text-xs" style={{ color: "#525252" }}>—</span>}
                                    </td>
                                    <td className="py-3">
                                        <div className="flex flex-wrap gap-1.5 items-center">
                                            <select value={u.role} disabled={busyId === u.id || isMe}
                                                    onChange={(e) => setRole(u, e.target.value)}
                                                    className="text-xs px-2 py-1 rounded-full border bg-white"
                                                    style={{ borderColor: "#E5E0D8", color: "#1E1B4B" }}
                                                    data-testid={`admin-users-role-${u.id}`}>
                                                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                            {u.is_warrior ? (
                                                <button onClick={() => setRole(u, "member")} disabled={busyId === u.id || isMe}
                                                        className="text-xs btn-ghost inline-flex items-center gap-1"
                                                        title="Demote to member"
                                                        data-testid={`admin-users-demote-${u.id}`}>
                                                    <UserMinus size={12} /> To Member
                                                </button>
                                            ) : (
                                                <button onClick={() => setRole(u, "warrior")} disabled={busyId === u.id}
                                                        className="text-xs btn-ghost inline-flex items-center gap-1"
                                                        title="Promote to warrior"
                                                        data-testid={`admin-users-promote-${u.id}`}>
                                                    <UserCheck size={12} /> To Warrior
                                                </button>
                                            )}
                                            {u.is_warrior && (
                                                u.featured ? (
                                                    <button onClick={() => setFeatured(u, false)} disabled={busyId === u.id}
                                                            className="text-xs btn-ghost inline-flex items-center gap-1"
                                                            data-testid={`admin-users-unfeature-${u.id}`}>
                                                        <Star size={12} /> Unfeature
                                                    </button>
                                                ) : (
                                                    <button onClick={() => setFeatured(u, true)} disabled={busyId === u.id}
                                                            className="text-xs btn-gold inline-flex items-center gap-1"
                                                            data-testid={`admin-users-feature-${u.id}`}>
                                                        <Star size={12} /> Feature
                                                    </button>
                                                )
                                            )}
                                            {inactive ? (
                                                <button onClick={() => setActive(u, true)} disabled={busyId === u.id}
                                                        className="text-xs btn-primary inline-flex items-center gap-1"
                                                        data-testid={`admin-users-activate-${u.id}`}>
                                                    <CheckCircle2 size={12} /> Activate
                                                </button>
                                            ) : (
                                                <button onClick={() => setActive(u, false)} disabled={busyId === u.id || isMe}
                                                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full"
                                                        style={{ background: "#7F1D1D", color: "#FDFBF7", opacity: (busyId === u.id || isMe) ? 0.5 : 1 }}
                                                        data-testid={`admin-users-deactivate-${u.id}`}>
                                                    <Ban size={12} /> Deactivate
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filtered.length === 0 && (
                    <div className="py-8 text-center text-sm" style={{ color: "#525252" }}>No users.</div>
                )}
            </div>
            <div className="mt-3 text-xs flex items-center gap-2" style={{ color: "#525252" }}>
                <Flame size={11} style={{ color: "#D4AF37" }} /> Featured warriors appear first on the landing page.
            </div>
        </div>
    );
}
